import Grid from 'apollo-react/components/Grid';
import TextField from 'apollo-react/components/TextField';
import Button from 'apollo-react/components/Button';
import Typography from '../../StyledTypography';
import Paper from 'apollo-react/components/Paper';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './styles.css';
import './FormStyles.css';

import {
  getUserById,
  mapIdsToUsers,
  createEmailObject,
} from './utils/userUtils';

// Import form section components
import HeadSelection from './components/FormSections/HeadSelection';
import ManagerSelection from './components/FormSections/ManagerSelection';
import EmployeeSelection from './components/FormSections/EmployeeSelection';

/**
 * @param {Object} node - Node to view: { id: 'node1', level: 1, name: 'Engineering', description: 'Tech team', manager: [1,2], head: [3,4], employees: [5,6,7] }
 * @param {Function} onCancel - Callback to close the modal : () => void
 * @param {Array} users - All available users: [{ id: 1, username: 'john', email: 'john@example.com' }, { id: 2, username: 'jane' }, ...]
 * @returns {JSX.Element}
 */

export default function DepartmentFormView({ node, onCancel, users = [] }) {
  const { t } = useTranslation();

  const [selectedManager, setSelectedManager] = useState(
    node?.manager
      ? node?.level === 1 && Array.isArray(node.manager)
        ? mapIdsToUsers(node.manager, users)
        : getUserById(node.manager, users)
      : node?.level === 1
        ? []
        : null
  );

  const [selectedEmployees, setSelectedEmployees] = useState(
    node?.employees ? mapIdsToUsers(node.employees, users) : []
  );

  const [selectedHeads, setSelectedHeads] = useState(
    node?.head && Array.isArray(node.head)
      ? mapIdsToUsers(node.head, users)
      : []
  );

  const [selectedSingleHead, setSelectedSingleHead] = useState(() => {
    // level 1: single head selection (backend returns array, take first)
    if (node?.level === 1 && node?.head) {
      if (Array.isArray(node.head) && node.head.length > 0) {
        return getUserById(node.head[0], users);
      } else if (!Array.isArray(node.head)) {
        return getUserById(node.head, users);
      }
    }
    // level 1: check for invited head (backend returns array, take first)
    if (node?.level === 1 && node?.invitedHead) {
      if (Array.isArray(node.invitedHead) && node.invitedHead.length > 0) {
        return node.invitedHead[0]; // return email string directly
      } else if (
        !Array.isArray(node.invitedHead) &&
        typeof node.invitedHead === 'string'
      ) {
        return node.invitedHead;
      }
    }
    // level 2: single head inherited from parent
    if (
      node?.level === 2 &&
      node?.head &&
      Array.isArray(node.head) &&
      node.head.length === 1
    ) {
      return getUserById(node.head[0], users);
    }
    return null;
  });

  /**
   * Effect hook - Updates local states when node data or users change
   * Converts node IDs back to user objects for display
   * Handles different data structures for different node levels
   * Triggers when node properties or users array changes
   */
  useEffect(() => {
    // Managers - merge existing and invited
    if (node?.manager || node?.invitedManager) {
      if (node?.level === 1) {
        const managerUsers = node.manager
          ? mapIdsToUsers(node.manager, users)
          : [];
        const invitedManagers = node.invitedManager
          ? node.invitedManager.map(createEmailObject)
          : [];
        setSelectedManager([...managerUsers, ...invitedManagers]);
      } else {
        // Single manager for level 2+
        if (node?.manager) {
          setSelectedManager(getUserById(node.manager, users));
        } else if (node?.invitedManager) {
          setSelectedManager(createEmailObject(node.invitedManager));
        } else {
          setSelectedManager(null);
        }
      }
    } else {
      setSelectedManager(node?.level === 1 ? [] : null);
    }

    // Employees - merge existing and invited
    const employeeUsers = node?.employees
      ? mapIdsToUsers(node.employees, users)
      : [];
    const invitedEmployees = node?.invitedEmployees
      ? node.invitedEmployees.map(createEmailObject)
      : [];
    setSelectedEmployees([...employeeUsers, ...invitedEmployees]);

    // Heads - handle single value for level 1, array for level 2+
    if (node.level === 1) {
      // level 1: single head value (backend may return array, extract first)
      if (node?.head) {
        const headId = Array.isArray(node.head) ? node.head[0] : node.head;
        const headUser = getUserById(headId, users);
        setSelectedSingleHead(headUser);
      } else if (node?.invitedHead) {
        // invitedHead may be array or string for level 1
        const invitedHeadEmail = Array.isArray(node.invitedHead)
          ? node.invitedHead[0]
          : node.invitedHead;
        setSelectedSingleHead(
          invitedHeadEmail ? createEmailObject(invitedHeadEmail) : null
        );
      } else {
        setSelectedSingleHead(null);
      }
    } else if ((node?.head && Array.isArray(node.head)) || node?.invitedHead) {
      // level 2+: array of heads or invited head email string (backend may return array, extract first if needed)
      const headUsers = node.head ? mapIdsToUsers(node.head, users) : [];
      const invitedHeads = node.invitedHead
        ? Array.isArray(node.invitedHead)
          ? node.invitedHead.map(createEmailObject)
          : [createEmailObject(node.invitedHead)]
        : [];
      const allHeads = [...headUsers, ...invitedHeads];

      setSelectedHeads(allHeads);

      if (node.level === 2 && allHeads.length === 1) {
        setSelectedSingleHead(allHeads[0]);
      } else if (node.level === 2 && node.head && node.head.length === 1) {
        setSelectedSingleHead(getUserById(node.head[0], users));
      } else if (node.level === 2 && node.invitedHead) {
        setSelectedSingleHead(
          Array.isArray(node.invitedHead)
            ? createEmailObject(node.invitedHead[0])
            : createEmailObject(node.invitedHead)
        );
      } else if (node.level === 2) {
        setSelectedSingleHead(null);
      }
    } else {
      setSelectedHeads([]);
      if (node.level === 2) {
        setSelectedSingleHead(null);
      }
    }
  }, [
    node?.manager,
    node?.invitedManager,
    node?.employees,
    node?.invitedEmployees,
    node?.head,
    node?.invitedHead,
    users,
  ]);

  return (
    <div>
      <div>
        <Grid container spacing={1} className={`form-grid modal-form`}>
          <Grid size={12}>
            <Typography variant="h4" className="form-title">
              {t('department_form')}
            </Typography>
          </Grid>

          <Grid size={12}>
            <Paper className={`form-paper modal-form`}>
              <Grid container spacing={1} className="full-width">
                <Grid size={12}>
                  <TextField
                    fullWidth
                    name="name"
                    label={t('department_name')}
                    value={node?.name || ''}
                    disabled
                  />
                </Grid>

                <Grid size={12}>
                  <TextField
                    fullWidth
                    name="description"
                    label={t('description')}
                    multiline
                    rows={2}
                    value={node?.description || ''}
                    disabled
                  />
                </Grid>

                <Grid size={12}>
                  <HeadSelection
                    node={node}
                    selectedHeads={selectedHeads}
                    selectedSingleHead={selectedSingleHead}
                    disabled
                    users={users}
                  />
                </Grid>

                <Grid size={12}>
                  <ManagerSelection
                    node={node}
                    selectedManager={selectedManager}
                    disabled
                  />
                </Grid>

                <Grid size={12}>
                  <EmployeeSelection
                    selectedEmployees={selectedEmployees}
                    disabled
                  />
                </Grid>

                <Grid size={12}>
                  <div className="form-buttons">
                    <Button
                      onClick={onCancel}
                      variant="outlined"
                      color="secondary"
                      size="large"
                      className="cancel-button"
                    >
                      {t('cancel')}
                    </Button>
                  </div>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      </div>
    </div>
  );
}
